/*
 * THE REDESIGN, PART 2: A DESIGN, IN A LIGHTBOX AT ITS OWN ADDRESS (2026-10-08), on assets/rd-finding.js.
 *
 * Daniel, 8 Oct: "it is a separate page but it opens in a lightbox. It can be a very big lightbox on a lot of the page
 * that is nevertheless good for SEO." So a design never navigates away from its list. A card clicked on an occasion
 * page, All cards, search results, a series or an artist opens here, over the list, and the address becomes the
 * design's own (/pages/occasions?view=cards&design=<id>&from=<occasion>). Back, Escape, the close button and a click outside
 * close it and leave the list exactly where it was. The design's address opened directly (a shared link, a search
 * engine) shows the same lightbox over the list it belongs to, its occasion's or All cards
 * (templates/page.cards.liquid renders that list), so the address stands on its own as a page.
 *
 * Built from Claude Design's Design Page (Part 2), changed by Daniel on 8 Oct: three views, Front, Inside, Back, in that
 * order (Photos is gone); from Front to Back and back again the card turns over, the share page's turn
 * (sections/shared-card.liquid .pp-share__flat: rotateY, perspective 1600px, .9s cubic-bezier(.3,.1,.2,1)). With
 * reduced motion every change is instant (Spin, a fourth view, came on 17 Oct: below). The card and its buttons are in
 * the first screen at 1280x720 and up, and on a 390x844 phone.
 *
 * INSIDE IS THE REAL CARD (Daniel, 8 Oct, replacing a flat mockup of the inside): the builder's seven 3D planes with
 * this design's art, and every move between Front, Inside and Back is the artist portal's (setView, below): Inside
 * opens the card, Back closes it and turns it over. Open and still with reduced motion. assets/rd-card3d.js, loaded with
 * card-stage.js, stage.css and the geometry the first time Inside is picked; released (out of the page, images let go)
 * when the box closes or moves to another design.
 *
 * ⭐ SPIN (17 Oct, Yonatan's v2e render): a fourth view, after Back, with the card open, floating, turning a full 360
 * degrees in a 4 s loop, this design's art on all eight planes and his shading over it (rd-card3d.js RDF3D.spin).
 * Nothing of it loads before Spin is picked (?show=spin opens on it); leaving the view stops the clip and lets its
 * decoder go, closing the box lets the clip go too. Reduced motion: the card still, facing you (frame 0). It is a
 * cross-fade, not a move: the flat card or the 3D card fades off the turning card and back over it (setView).
 *
 * ⭐ FRONT, INSIDE AND BACK ARE FOUR STATES OF ONE CARD, AND A SWITCH PLAYS THE MOVE (17 Oct, Daniel: "i asked you
 * specifically to have the animations between each of the states for the product page: close, open, turn over, and
 * spin floating"). Yonatan's v2e clips (open, close, turn over and back) with the design's art on the planes, posed
 * from the clip's own frames (rd-card3d.js RDF3D.views); Inside to Back fades to the shut card and turns it over,
 * Back to Inside fades to the shut Front and opens it, and a click mid-move goes back from where the card is. Each state
 * frames its own card large; the shut card tilts to the pointer with the Picture Card's light; a click on the card
 * zooms (in every state), and only the tabs change the view. Spin is cross-faded in and out. The flat card
 * is the instant placeholder for Front until the card is ready, at the shut card's place. Today's flat turn and the
 * builder-pose Inside with its opening stay behind a switch, ?ppclips=0 (remembered in this browser, localStorage
 * pp_ppclips; ?ppclips=1 ends it), as design options always do (Daniel, 7 Oct).
 *
 * THE OCCASION COMES TOO. "Design a card" opens the builder with this design (?design=) on the occasion the visitor came
 * from (&from=, kept when the design is placed on it), else the one it was made for, else the first its cover suits,
 * else Just because for a generic card; ?for= carries the occasion the inside top's words are for (configurator.js
 * CARRIED_OCC). Never the occasion a stripped inside greeting was once written for (`greets`, the catalogue's
 * topGreets): Daniel, 8 Oct, "There should only ever be an occasion set for a card if the cover or the cover-inside
 * combination specifically have something to do with a specific occasion" (venture decisions.md, 2026-10-08). The
 * greeting only decides which inside top shows: as designed on an occasion it was written for, else the text-free top.
 *
 * ⭐ AND ITS WORDS ARE INSIDE (Daniel, 8 Oct): "if this hyacinth macaw is picked in the occasion page for father's day,
 * we can have on the inside happy father's day ... All that matters is which occasion it's picked for." On a design
 * with a text-free top, shown for an occasion its greeting was not written for, the 3D Inside draws that occasion's
 * words on the top exactly as the builder starts them: inside-top.js (window.PPTop) is the builder's own renderer,
 * with its default template, its words (PPTop.startSpec), its fit inside the 8 mm margin and the design's topBox, and
 * the builder's faces (snippets/pp-top-fonts.liquid). Both load with the 3D card. With no occasion (All cards, a
 * search, a series, an artist), the no-occasion pair, "For a good friend / So glad you are in my life", and ?for=any
 * tells the builder so: Just because stays the ORDER's occasion for a generic card (occasionFor, routing), and the
 * words do not follow it (wordsOccFor). What a visitor sees inside here is what the builder opens with.
 */
(function () {
  'use strict';
  const F = window.RDF;
  if (!F) return;
  const esc = F.esc;
  const phone = () => window.matchMedia('(max-width: 760px)').matches;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* ⭐ A SET'S PAGE (sections/pp-set.liquid, the reviewers.com set; Daniel, 19 Oct: "can't we have the lightbox product
     page for the Reviewers.com product as well"). The same box over the set's own page, at <the page>?design=<id>, and
     nothing in it leads out of the set: no breadcrumb, series, What it suits, More from, or artist links, and its button
     opens the builder with the set carried (PPSet.builderUrl). */
  const SET = (function () { const r = document.querySelector('[data-pps]'); return r && window.PPSet ? r.getAttribute('data-pps') : ''; })();
  const isDesign = SET
    ? (u) => { const x = new URL(u, location.href); return x.pathname === location.pathname && !!x.searchParams.get('design'); }
    : F.isDesignHref;
  /* The four states played by the clips (17 Oct, below): on unless ?ppclips=0 turned them off in this browser. */
  const CLIPS_ON = (function () {
    try {
      const m = /[?&]ppclips=(0|1)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_ppclips', '0'); else localStorage.removeItem('pp_ppclips'); }
      return localStorage.getItem('pp_ppclips') !== '0';
    } catch (e) { return !/[?&]ppclips=0(?:&|$)/.test(location.search); }
  })();
  /* ⭐ THE CARD SHOWS ITSELF (Daniel, 19 Oct: "when a lightbox is opened, wait maybe a second and then have the card
     open on its own ... so that when someone chooses a design, they can see the opening animation"). A box opened on
     Front opens the card to Inside after AUTO_MS, holds, then spins (Daniel, 19 Oct, the same day: "let's do the auto
     spin as well"; it was open only for an hour). The other options stay as switches, as design options do (7 Oct):
     ?ppauto=open opens and stays open; ?ppauto=0 never moves on its own; ?ppauto=1 back to the default. Remembered in
     this browser (localStorage pp_ppauto). Any touch, click, key or wheel in the box stops it, and reduced motion
     never starts it.
     ⛔ OFF BY DEFAULT since 24 Oct (Daniel: "don't do the open and spin automatically anymore"): the card waits on
     Front. ?ppauto=spin or ?ppauto=open turns it back on in this browser; ?ppauto=0 (or =1, the old default's switch)
     returns to waiting. */
  const AUTO = (function () {
    try {
      const m = /[?&]ppauto=(0|1|open|spin)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0' || m[1] === '1') localStorage.removeItem('pp_ppauto'); else localStorage.setItem('pp_ppauto', m[1]); }
      return localStorage.getItem('pp_ppauto') || '0';
    } catch (e) { const m = /[?&]ppauto=(open|spin)(?:&|$)/.exec(location.search); return m ? m[1] : '0'; }
  })();
  const AUTO_MS = 1000, AUTO_SPIN_HOLD_MS = 1200;
  let autoT = 0, autoTok = 0;
  function autoStop() { autoTok++; clearTimeout(autoT); autoT = 0; }
  function autoStart(d) {
    autoStop();
    if (AUTO === '0' || reduced()) return;
    const tok = autoTok;
    const live = () => tok === autoTok && !lb.hidden && current === d;
    autoT = setTimeout(() => {
      if (!live() || view !== 'front') return;
      setView('inside', true);
      if (AUTO !== 'spin') return;
      /* Spin once the opening has played: the card of the states ready and still, then a short hold. 12 s at most, so a
         card that never loads stays open on Inside rather than jumping to Spin out of nowhere. */
      const t0 = performance.now();
      const R = () => window.RDF3D && window.RDF3D.views;
      const wait = () => {
        if (!live() || view !== 'inside') return;
        const settled = CLIPS_ON ? (vwReady && R() && !R().moving()) : shown3d();
        if (settled) { autoT = setTimeout(() => { if (live() && view === 'inside') setView('spin', true); }, AUTO_SPIN_HOLD_MS); return; }
        if (performance.now() - t0 < 12000) requestAnimationFrame(wait);
      };
      requestAnimationFrame(wait);
    }, AUTO_MS);
  }
  let draft = false;
  try { draft = localStorage.getItem('pp_draft_flag_v1') === '1'; } catch (e) { /* private mode */ }

  /* ---- the box ------------------------------------------------------------------------------------------------ */
  const lb = document.createElement('div');
  lb.className = 'rdf-lb';
  lb.hidden = true;
  lb.innerHTML = '<div class="rdf-lb__scrim" data-lb-close></div>' +
    '<div class="rdf-lb__panel rdf" role="dialog" aria-modal="true" aria-labelledby="rdf-lb-title" tabindex="-1" data-lb-panel>' +
      '<div class="rdf-lb__bar"><button type="button" class="rdf-x rdf-lb__x" data-lb-close aria-label="Close"><span class="ic ic-x" aria-hidden="true"></span></button></div>' +
      '<div data-lb-body></div></div>';
  document.body.appendChild(lb);
  const panel = lb.querySelector('[data-lb-panel]'), body = lb.querySelector('[data-lb-body]');
  let current = null, view = 'front', opener = null, depth = 0, closing = false, listHref = '';

  /* The occasion the builder opens on: the one the visitor came from, else made for, suits, or generic (Just because),
     the same order as the builder's own (design-picker.js designOccasion). Never `greets`, and nothing for a design that
     is none of the three. */
  function occasionFor(d, from) {
    return wordsOccFor(d, from) || (d.any ? 'just-because' : '');
  }
  /* The occasion the inside top's WORDS are for: the same, without the generic card's Just because, which is routing and
     not an occasion anybody picked. '' is no occasion: the no-occasion pair (inside-top.js words). */
  function wordsOccFor(d, from) {
    if (from && d.occ.includes(from) && F.OCC_BY[from]) return from;
    const known = (h) => h && F.OCC_BY[h];
    return d.made.find(known) || d.suits.find(known) || '';
  }

  /* The stage: the card that turns over (Front, Back), and the real card, opening (Inside). */
  let insideArt = null;
  const EDIT_BELOW_MM = 14;   // the note's top, mm below the middle of the words band: under a title and its subtitle
  /* Only in a words band with room under the words (the blank-panel band, 10-60 mm: 551 of 589 editable designs on
     18 Oct). A short band (8-15 to 8-25 mm, the punny one-liners) has the art right under it, and the note sat on it. */
  const EDIT_MIN_BAND_MM = 30;
  function stage(d, wocc) {
    // As designed only on an occasion its stripped greeting was written for; with no occasion, the text-free top.
    // names: a greeting that names a person ("Happy Birthday, Mom", catalogue topNames) shows the occasion's words (2026-10-25).
    const own = !d.topPlain || (!!wocc && !d.names && d.greets.includes(wocc));
    const words = (d.text && d.text.inside) || '';
    const backAlt = 'The back of the card' + (d.se && d.seNo ? ', ' + d.se + ' No. ' + (parseInt(d.seNo, 10) || d.seNo) + ' of ' + (parseInt(d.seOf, 10) || d.seOf) : '');
    /* The design's own art for the 3D card. The inside top keeps its rule: a card whose greeting was stripped shows its
       text-free top on an occasion the greeting was not written for, with the occasion's words drawn on it as the builder
       starts them (drawTop), in the words band (the catalogue's topBox, trim mm from the top of 120; else the builder's
       blank-panel box, inside-top.js BLANK_BOX 10-60). Only if the drawing cannot be made (the renderer did not load)
       does it show bare, with the hint that the words there are theirs, centred in that band. */
    const box = d.topBox || [10, 60];
    insideArt = { key: d.id + (own ? '' : '|' + (wocc || 'any')), front: d.front, top: own ? d.top : d.topPlain, yours: !own,
      yoursAt: ((box[0] + box[1]) / 2 / 120) * 100, strip: d.strip, ground: d.bottom, msg: d.msg || '',
      plain: own ? '' : d.topPlain, wordsOcc: own ? null : wocc, box: d.topBox || null, blob: '', back: d.back || '',
      // "Yours to edit" (rd-card3d.js EDIT_NOTE): only where the builder lets the top's words change, under the words.
      edit: !!d.topPlain && box[1] - box[0] >= EDIT_MIN_BAND_MM, editAt: ((box[0] + box[1]) / 2 + EDIT_BELOW_MM) / 120 * 100 };
    const label = (own ? (words ? 'the top panel reads “' + words.replace(/\s*\/\s*/g, ' ') + '”' : 'the top panel') : topLabel(insideArt)) +
      (d.topPlain ? ', words you can change' : '');
    // The turning card first: it is under the flat card and the 3D card, which fade off it and back over it.
    return '<div class="rdf-spin" data-spin hidden aria-hidden="true" role="img" aria-label="The card open, turning round once, with this design inside and out"></div>' +
      '<div class="rdf-flip" data-flip><div class="rdf-flip__flat">' +
        '<div class="rdf-flip__face is-front">' + F.pictureCard(d.front, { motion: phone() || reduced() ? 'none' : 'tilt', eager: true, full: true, alt: d.alt.front || d.n }) + '</div>' +
        '<div class="rdf-flip__face is-back">' + (d.back ? '<img src="' + esc(d.back) + '" alt="' + esc(backAlt) + '"><span class="rdf-paper" aria-hidden="true"></span>' : '<span>The back of this card is plain.</span>') + '</div>' +
      '</div></div>' +
      '<div class="rdf-inside" data-inside hidden aria-hidden="true" role="img" aria-label="' + esc(insideLabel(label)) + '"></div>' +
      // The card of the four states (CLIPS_ON), over the flat card, which stands in for it until it is ready.
      (CLIPS_ON ? '<div class="rdf-vw" data-vw hidden aria-hidden="true" role="img" aria-label="' + esc(d.alt.front || d.n) + '"></div>' : '') +
      '<p class="rdf-inside__wait" data-inside-wait hidden></p>' +
      '<button type="button" class="rdf-arrow is-prev" data-step="-1"><span class="ic ic-chev-l" aria-hidden="true"></span></button>' +
      '<button type="button" class="rdf-arrow is-next" data-step="1"><span class="ic ic-chev-r" aria-hidden="true"></span></button>' +
      '<div class="pp-hovertag rdf-tag" data-rdf-tag aria-hidden="true"></div>' +
      '<p class="rdf-lookhint" data-rdf-hint aria-hidden="true" hidden></p>';
  }
  const insideLabel = (top) => 'Inside the card: ' + top + ', four photo windows, and the note panel';

  /* ⭐ THE OCCASION'S WORDS ON THE TEXT-FREE TOP, AS THE BUILDER STARTS THEM (Daniel, 8 Oct). The builder's renderer and
     its starting spec (inside-top.js startSpec: template, words, no subtitle in a one-line band; optsFor: the band and
     its background rule), drawn on the design's text-free top at the builder's own size and encoding (configurator.js
     _topRenderNow: 1000 px wide, JPEG 0.9), its faces loaded first (never measured against a fallback). Resolves
     once art.top is the drawing, or as it was when it cannot be made: then the bare top and its hint show. */
  function topSpecFor(art) {
    const P = window.PPTop, O = art.wordsOcc ? F.OCC_BY[art.wordsOcc] : null;
    return P.startSpec(art.wordsOcc || '', O ? O.headline : '', art.box || P.BLANK_BOX);
  }
  // What the top reads: the words drawn (or about to be), else the bare top's room for the visitor's own.
  function topLabel(art) {
    if (!window.PPTop || (art.drawing && art.yours)) return 'the top panel, with room for your own words or a photo';
    const sp = topSpecFor(art);
    return 'the top panel reads “' + sp.title + (sp.subtitle ? '. ' + sp.subtitle : '') + '”';
  }
  function drawTop(art) {
    const P = window.PPTop;
    if (!P || art.wordsOcc == null || !art.plain) return Promise.resolve();
    if (art.drawing) return art.drawing;
    const spec = topSpecFor(art), opts = P.optsFor(art.box || P.BLANK_BOX);
    const base = new Promise((ok) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';    // the catalogue and the theme's own files answer CORS: the canvas stays readable
      img.onload = () => ok(img);
      img.onerror = () => ok(null);
      img.src = art.plain;
    });
    art.drawing = Promise.all([base, P.ensureFonts(spec)]).then(([img]) => {
      if (!img) return null;
      const r = P.preview(img, spec, opts, 1000);
      return new Promise((ok) => r.canvas.toBlob((b) => {
        r.canvas.width = r.canvas.height = 0;
        if (b) { art.blob = URL.createObjectURL(b); art.top = art.blob; art.yours = false; }
        ok();
      }, 'image/jpeg', 0.9));
    }).catch(() => null);
    return art.drawing;
  }
  // The drawing a design's box made, let go with the box (show, hide).
  function dropArt() {
    if (insideArt && insideArt.blob) URL.revokeObjectURL(insideArt.blob);
    insideArt = null;
  }

  /* THE REAL CARD (Daniel, 8 Oct: "the animated opening with the real dimensions that we worked so hard to create").
     assets/rd-card3d.js plays it: the share page's opening on the builder's seven planes. Nothing of it loads until
     somebody first picks Inside: the player, card-stage.js, stage.css and the geometry, from the addresses
     layout/redesign.liquid puts on window.RDF_CARD; the shading still comes with the card. */
  /* The player, card-stage.js and stage.css come through rd-finding.js (F.stage3d), which the tiles' gallery hover
     shares, so the two never add them twice; with them the builder's inside-top renderer, for the occasion's words on a
     text-free top (drawTop). ⚠️ Its faces are declared on the page (pp-top-fonts) and fetched only when drawn with. Not
     needed for a card to show: without it the text-free top shows bare, with its hint. */
  let piecesP = null;
  function pieces() {
    if (piecesP) return piecesP;
    const A = window.RDF_CARD || {};
    piecesP = Promise.all([F.stage3d(), window.PPTop || !A.top ? null : F.addTag('script', { src: A.top, async: '' }).catch(() => null)])
      .then(() => { if (!window.RDF3D || !window.PP || !window.PP.CardStage) throw new Error('no stage'); });
    piecesP.catch(() => { piecesP = null; });   // asking again tries again
    return piecesP;
  }
  let loader = null;
  function card3d() {
    if (loader) return loader;
    const A = window.RDF_CARD || {};
    loader = Promise.all([pieces(), F.json(A.geom)]).then((r) => { window.RDF3D.init(r[1], A.shade); });
    loader.catch(() => { loader = null; });   // picking Inside again tries again
    return loader;
  }
  /* The four states: both geometries and the clips' addresses (RDF_CARD), once, when the box first opens. */
  let viewsLoader = null;
  function views3d() {
    if (viewsLoader) return viewsLoader;
    const A = window.RDF_CARD || {};
    const clip = (k) => ({ big: A[k], small: A[k + 'Sm'] || A[k] });
    viewsLoader = (A.geom && A.backGeom && A.open ? Promise.all([pieces(), F.json(A.geom), F.json(A.backGeom)]) : Promise.reject(new Error('no clips'))).then((r) => {
      window.RDF3D.views.init(r[1], r[2], { open: clip('open'), close: clip('close'), back: clip('back'), rev: clip('backRev') }, { big: A.shutRest, small: A.shutRestSm });
    });
    viewsLoader.catch(() => { viewsLoader = null; });
    return viewsLoader;
  }
  /* Spin's geometry and its clips' addresses, on top of the card (the clip itself is fetched by the player). */
  let spinLoader = null;
  function spin3d() {
    if (spinLoader) return spinLoader;
    const A = window.RDF_CARD || {};
    spinLoader = Promise.all([pieces(), A.spinGeom ? F.json(A.spinGeom) : Promise.reject(new Error('no spin'))]).then((r) => {
      window.RDF3D.spin.init(r[1], { big: A.spin, small: A.spinSm || A.spin });
    });
    spinLoader.catch(() => { spinLoader = null; });
    return spinLoader;
  }
  const release3d = () => {
    const R = window.RDF3D;
    if (!R) return;
    R.release();
    if (R.spin) R.spin.release();
    if (R.views) R.views.release();
  };

  /* ---- FRONT, INSIDE, BACK: THE ARTIST PORTAL'S MOVES (Daniel, 8 Oct) -----------------------------------------------
     ⚠️ Since 17 Oct only with ?ppclips=0: by default these three views are the four states of one card (clipsView).
     "take the behavior from what we have on the artist platform … When you click inside, the front opens and shows you
     the inside. Then you have back. If you click that, the card needs to close and then flick back to the back. There
     should always be an animation from state to state."

     The portal (artists-worker src/views.js cardPreview, and src/preview.js sharePage) has Open, Closed and Back; here
     they are Inside, Front and Back. Its moves, with its own numbers:
       Front -> Inside   the shut card opens: lies back, unfolds (rd-card3d.js, the portal's switch: 0.62 s + 0.9 s),
                       the 3D card taking the flat card's place as it starts (show3d, 'handover')
       Inside -> Front   it closes: folds, stands up
       Inside -> Back    it closes; once it is still, the flat card takes its place (a .35 s cross-fade, laid exactly
                         over the shut card) and 380 ms later turns round (.9 s, the turn Front and Back always had)
       Back -> Inside    the flat card turns back; 900 ms later the 3D card takes its place and opens (the portal
                         cross-fades .35 s into the opening there; here it is the same quick handover as from Front)
       Front <-> Back    the flat card turns (on the portal, from Closed: the same turn after the same cross-fade; here
                         Front already IS the flat card, so it simply turns)
       Back -> Front     it turns back
     Picking the view already shown does nothing, as on the portal. A press in the middle of a move takes over from
     wherever the card is: an open or close reverses, and a turn turns back.

     ⚠️ ONE ADDITION THE PORTAL HAS NO NEED OF: Front here is the flat card (the portal's Closed is the 3D card), so after
     Inside -> Front the shut 3D card hands back to it with the same cross-fade the portal uses for Back. The flat card
     carries the page's shadow and is light to keep on screen; the 3D card loads only when Inside is first picked.
     The portal waits for the shut card to be still (four equal readings, 80 ms apart) before the cross-fade; SETTLE_MS
     is that wait, measured from the moment it lands.

     Reduced motion: every view is shown at once, the inside as the open card, still. */
  const FADE_MS = 350, TURN_MS = 900, TURN_AFTER_FADE = 380, SETTLE_MS = 320;
  let timers = [], openTok = 0, hideT = 0;
  const later = (fn, ms) => { timers.push(setTimeout(fn, ms)); };
  const clearLater = () => { timers.forEach(clearTimeout); timers = []; };
  const parts = () => ({ stage: body.querySelector('[data-rdf-stage]'), flip: body.querySelector('[data-flip]'), inside: body.querySelector('[data-inside]'), spin: body.querySelector('[data-spin]') });
  const shown3d = () => { const st = parts().stage; return !!(st && st.classList.contains('is-3d')); };

  /* The flat card's box, about the stage's centre: layout metrics, so a tilt or a turn on it changes nothing. */
  function flatBox() {
    const { stage: st, flip } = parts();
    return { cx: flip.offsetLeft + flip.offsetWidth / 2 - st.clientWidth / 2, cy: flip.offsetTop + flip.offsetHeight / 2 - st.clientHeight / 2, h: flip.offsetHeight };
  }

  /* The cross-fade between the flat card and the 3D card. The 3D card leaves the layout once it has faded out, so its
     layers are not kept while the flat card is showing.
     Back to the flat card it is the portal's .35 s, with the 3D card shut and still. Onto the 3D card it is a handover
     ('handover'): the shut 3D card is on the flat card's box already, so it is shown at once, on top, and the flat card
     goes from under it in .15 s, the first .15 s of the opening, while the cover has barely moved. A plain cross-fade
     there ran into the opening and showed both cards at half strength (seen 2026-10-08; on the portal this handover
     happens only from Back, and there it does show). */
  function show3d(on, instant) {
    const { stage: st, flip, inside } = parts();
    if (!st) return;
    clearTimeout(hideT);
    st.classList.toggle('is-instant', instant === true);
    st.classList.toggle('is-handover', instant === 'handover');
    if (on) { inside.hidden = false; void inside.offsetWidth; }
    st.classList.toggle('is-3d', on);
    inside.setAttribute('aria-hidden', String(!on));
    flip.setAttribute('aria-hidden', String(on));
    if (!on) hideT = setTimeout(() => { if (!st.classList.contains('is-3d')) inside.hidden = true; }, instant ? 0 : FADE_MS + 50);
  }

  /* The flat card's turn: Front and Back, as it always was (the share page's .pp-share__flat turn). */
  function turn(toBack, instant) {
    const { flip } = parts();
    flip.classList.toggle('is-still', !!instant);
    void flip.offsetWidth;
    flip.classList.toggle('is-turned', toBack);
  }

  /* To Inside: the 3D card loads (the first time), takes the flat card's place shut, and opens. */
  function open3d(instant) {
    const { inside: host } = parts(), art = insideArt, tok = ++openTok;
    if (!host || !art) return;
    const wait = body.querySelector('[data-inside-wait]');
    const say = (t) => { if (wait) { wait.textContent = t; wait.hidden = !t; } };
    const ok = () => tok === openTok && !lb.hidden && view === 'inside' && insideArt === art && host.isConnected;
    const slow = setTimeout(() => { if (ok()) say('Opening the card…'); }, 600);
    card3d().then(() => (ok() ? drawTop(art) : null)).then(() => {
      // What the top reads, now the words are known (the renderer loads with the card).
      if (ok() && art.wordsOcc != null) host.setAttribute('aria-label', insideLabel(topLabel(art)));
      return ok() ? window.RDF3D.mount(host, art) : null;
    }).then(() => {
      clearTimeout(slow);
      if (!ok()) return;
      say('');
      const R = window.RDF3D;
      host.hidden = false;
      if (instant || reduced()) { if (R.still()) show3d(true, true); lookSync(); return; }
      if (!shown3d()) { if (!R.shut(flatBox())) return; show3d(true, 'handover'); }   // the flat card hands over, shut...
      lookSync();
      R.open(lookSync);                                                  // ...and the card opens
    }).catch(() => {
      clearTimeout(slow);
      if (host.isConnected && view === 'inside') say('The card didn’t load. Pick Inside to try again.');
    });
  }

  /* From Inside: the card closes; once it is still, the flat card takes its place, then `next` (the turn, for Back). */
  function close3d(next) {
    const R = window.RDF3D;
    if (!R || !shown3d()) { if (next) next(); return; }
    R.close(flatBox(), () => later(() => { show3d(false); if (next) next(); lookSync(); }, SETTLE_MS));
  }

  /* ---- THE FOUR STATES (17 Oct; CLIPS_ON): Front, Inside and Back played by Yonatan's clips (rd-card3d.js VIEWS) ------
     The card (.rdf-vw) is built when the box opens: both geometries, the design's art and the clip that holds Front's
     frame, all four clips fetched as blobs at once. Until it is ready the flat card stands in, moved and sized onto the
     shut card's place in the clips' framing (vwShut), so the card that replaces it is in the same place; then .rdf-vw
     fades in over it (.is-vw) and stays, for every view but Spin. A view picked before the card is ready is played to as
     soon as it is (an address with ?show= opens on Front and then plays to it, as the button would).
     .rdf-vw isolates the clip's multiply on the stage's own ground (rd-finding.css), so it may fade as a whole. */
  let vwReady = false;
  const vwEl = () => body.querySelector('[data-vw]');
  // The 1000 px clips for a phone or a stage under 1100 device px, as Spin's.
  const vwSmall = () => {
    const st = parts().stage;
    return phone() || !st || Math.min(st.clientWidth, st.clientHeight) * Math.min(2, window.devicePixelRatio || 1) <= 1100;
  };
  // The flat card onto the shut card's place and size in the clips' framing (it eases there if it is on screen).
  function vwShut() {
    const { stage: st, flip } = parts(), b = window.RDF3D.views.shutBox(st.clientWidth, st.clientHeight);
    if (!b || !flip) return;
    const h = b.h, w = h * flip.offsetWidth / (flip.offsetHeight || 1);
    flip.classList.add('is-placed');
    flip.style.width = w.toFixed(1) + 'px';
    flip.style.translate = b.cx.toFixed(1) + 'px ' + b.cy.toFixed(1) + 'px';
  }
  function vwRest() { if (view !== 'spin') lookSync(); }
  function clipsView(v, animate) {
    const { stage: st } = parts(), host = vwEl(), art = insideArt;
    if (!host || !art) return;
    const instant = !animate || reduced();
    if (vwReady) { window.RDF3D.views.go(v, instant); lookSync(); return; }
    if (clipsView.busy === art) return;            // loading: the view picked is read when it is ready
    clipsView.busy = art;
    const tok = openTok;
    const ok = () => !lb.hidden && insideArt === art && host.isConnected;
    host.hidden = false;                           // laid out (measured) but not seen until .is-vw
    views3d().then(() => {
      if (!ok()) return null;
      vwShut();
      return drawTop(art);
    }).then(() => (ok() ? window.RDF3D.views.mount(host, art, 'front', { small: vwSmall(), onRest: vwRest }) : null)).then((shown) => {
      if (clipsView.busy === art) clipsView.busy = null;
      if (!shown || !ok()) return;
      vwReady = true;
      if (art.wordsOcc != null) host.setAttribute('aria-label', insideLabel(topLabel(art)));
      if (view === 'spin') host.hidden = true;     // loaded under Spin: out of the layout until a state is picked
      else {
        st.classList.add('is-vw');
        host.setAttribute('aria-hidden', 'false');
        parts().flip.setAttribute('aria-hidden', 'true');
        if (view !== 'front') later(() => { if (view !== 'spin') window.RDF3D.views.go(view, reduced()); }, reduced() ? 0 : FADE_MS);
      }
      lookSync();
    }).catch(() => { if (clipsView.busy === art) clipsView.busy = null; /* the flat card stays: Front and Back as before */ });
    void tok;
  }

  /* ---- SPIN (17 Oct): a cross-fade, never a move ------------------------------------------------------------------
     To Spin: the player loads (the first time), the turning card is laid out under the view on screen (laid out, as it
     is measured, but not seen: rd-finding.css .rdf-spin), and once its clip is in and its first frame decoded, the view
     on top fades off it (.35 s, the portal's fade). Covered, the 3D card leaves the layout and the flat card goes back to
     its front, so leaving Spin always starts from a known card.
     Away from Spin: the flat card (turned to the face picked) fades back over the turning card, which stops once it is
     covered (spinOffT, its own timer: a later setView must not cancel the stop, or the clip would loop on, unseen). For
     Inside it is the front that fades back, and then opens, as from Front. ⛔ The turning card itself never fades: its
     shading is a multiply blend, and an opacity under 1 would print the clip's white as a white square (rd-card3d.js). */
  let spinOffT = 0;
  // The 1000 px clip for a phone or a square under 1100 device px (the square is the stage's shorter side).
  const spinSmall = () => {
    const st = parts().stage;
    return phone() || !st || Math.min(st.clientWidth, st.clientHeight) * Math.min(2, window.devicePixelRatio || 1) <= 1100;
  };
  function toSpin() {
    const { stage: st, spin: host } = parts(), art = insideArt, tok = ++openTok;
    if (!st || !host || !art) return;
    clearTimeout(spinOffT);
    st.classList.remove('is-spinout');
    const wait = body.querySelector('[data-inside-wait]');
    const say = (t) => { if (wait) { wait.textContent = t; wait.hidden = !t; } };
    const ok = () => tok === openTok && !lb.hidden && view === 'spin' && insideArt === art && host.isConnected;
    st.classList.remove('is-instant', 'is-handover');   // the view on screen fades off at the portal's pace
    host.hidden = false;
    const slow = setTimeout(() => { if (ok()) say('Loading the turn…'); }, 600);
    spin3d().then(() => (ok() ? drawTop(art) : null))
      .then(() => (ok() ? window.RDF3D.spin.mount(host, art, { small: spinSmall(), still: reduced() }) : false))
      .then((shown) => {
        clearTimeout(slow);
        if (!ok() || !shown) return;
        say('');
        st.classList.add('is-spin');
        host.setAttribute('aria-hidden', 'false');
        parts().flip.setAttribute('aria-hidden', 'true');
        parts().inside.setAttribute('aria-hidden', 'true');
        later(() => {
          if (view !== 'spin') return;
          if (shown3d()) { st.classList.remove('is-3d'); parts().inside.hidden = true; }
          turn(false, true);
          if (CLIPS_ON && vwEl()) vwEl().hidden = true;   // the card leaves the layout under Spin (no raster kept)
          // Opened on Spin (?show=spin): the card of the other states loads now, after Spin's clip, so a tab has it ready.
          if (CLIPS_ON && !vwReady) clipsView('front', false);
        }, FADE_MS + 50);
      })
      .catch(() => {
        clearTimeout(slow);
        if (host.isConnected && view === 'spin') say('The card didn’t load. Pick Spin to try again.');
      });
  }
  function fromSpin(v, animate) {
    const { stage: st, spin: host, flip } = parts();
    clearTimeout(spinOffT);
    st.classList.remove('is-instant', 'is-handover');
    if (CLIPS_ON) {
      // The card back in the layout, set at once at the view picked (its rest frame decoded), then faded back over the
      // turning card, which stops once it is covered. Before the card is ready: the flat card, as on opening.
      const back = () => {
        if (view === 'spin') return;
        host.setAttribute('aria-hidden', 'true');
        st.classList.toggle('is-instant', !animate);
        st.classList.add('is-spinout');
        st.classList.remove('is-spin');
        spinOffT = setTimeout(() => {
          st.classList.remove('is-spinout', 'is-instant');
          if (view === 'spin' || !host.isConnected) return;
          if (window.RDF3D && window.RDF3D.spin) window.RDF3D.spin.stop();
          host.hidden = true;
        }, animate ? FADE_MS + 50 : 0);
      };
      const card = vwEl();
      if (vwReady && card) {
        card.hidden = false;
        const R = window.RDF3D.views;
        R.go(v, true);
        const tok = openTok, t0 = performance.now();
        const wait = () => { if (tok !== openTok) return; if (!R.moving() || performance.now() - t0 > 1500) back(); else requestAnimationFrame(wait); };
        requestAnimationFrame(wait);
      } else { turn(v === 'back', true); back(); clipsView(v, animate); }
      return;
    }
    if (v !== 'inside' && shown3d()) { st.classList.remove('is-3d'); parts().inside.hidden = true; }   // left within the fade
    turn(v === 'back', true);
    flip.setAttribute('aria-hidden', String(shown3d()));
    host.setAttribute('aria-hidden', 'true');
    st.classList.toggle('is-instant', !animate);
    st.classList.add('is-spinout');           // the turning card stays seen while the view fades back over it
    st.classList.remove('is-spin');
    spinOffT = setTimeout(() => {
      st.classList.remove('is-spinout');
      if (view === 'spin' || !host.isConnected) return;
      if (window.RDF3D && window.RDF3D.spin) window.RDF3D.spin.stop();
      host.hidden = true;
    }, animate ? FADE_MS + 50 : 0);
    if (v === 'inside') later(() => open3d(!animate), animate ? FADE_MS : 0);
  }

  function setView(v, animate) {
    const { flip } = parts();
    if (!flip) return;
    const was = view;
    if (animate && v === was) return;            // the view already shown: nothing, as on the portal
    view = v;
    if (zoomedNow()) R3().zoomOut(true);
    if (TAG) TAG.off();
    body.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('data-view') === v)));
    syncArrows();
    clearLater();
    openTok++;
    const waitEl = body.querySelector('[data-inside-wait]');
    if (waitEl) waitEl.hidden = true;
    lookSync();
    if (v === 'spin') { toSpin(); return; }
    if (was === 'spin') { fromSpin(v, !!animate && !reduced()); return; }
    if (CLIPS_ON) { clipsView(v, animate); return; }
    if (!animate || reduced()) {
      turn(v === 'back', true);
      if (v === 'inside') open3d(true);
      else show3d(false, true);
      return;
    }
    const turned = flip.classList.contains('is-turned');
    if (v === 'back') {
      if (shown3d()) close3d(() => later(() => turn(true), TURN_AFTER_FADE));
      else turn(true);
    } else if (v === 'front') {
      if (turned) turn(false);
      if (shown3d()) close3d(null);
    } else if (turned) {
      turn(false);
      later(() => open3d(false), TURN_MS);
    } else {
      open3d(false);
    }
  }

  /* THE TABS: Front, Inside, Back, Spin, and the box opens on Front. (For a few hours on 18 Oct it opened on Spin with
     tabs Spin, Closed, Open, Back: a request meant for the share page. ?show= still reads those names, so a link written
     then still works; the address writes front, inside, back, spin, and nothing for Front, the default.) */
  const TABS = [['front', 'Front'], ['inside', 'Inside'], ['back', 'Back'], ['spin', 'Spin']];
  const SHOW_IN = { spin: 'spin', closed: 'front', front: 'front', open: 'inside', inside: 'inside', back: 'back' };
  /* THE ARROWS AND THE ARROW KEYS (Daniel, 18 Oct: "let's add arrows in design lightbox page in case people miss the
     tabs at the bottom. and let's move between states with arrows left and right"): the previous and next view in the
     tabs' order, wrapping (Spin to Front, Front back to Spin). They press that view's tab, so they take exactly its path
     (the clips, the fades, a reversal mid-move, a fast run of presses) and the tabs stay in step. */
  function stepView(d) {
    const i = TABS.findIndex((t) => t[0] === view), n = TABS[(i + d + TABS.length) % TABS.length][0];
    const b = body.querySelector('[data-view="' + n + '"]');
    if (b) b.click();
  }
  function syncArrows() {
    const i = TABS.findIndex((t) => t[0] === view);
    body.querySelectorAll('[data-step]').forEach((a) => {
      const d = +a.getAttribute('data-step'), n = TABS[(i + d + TABS.length) % TABS.length];
      a.setAttribute('aria-label', (d > 0 ? 'Next: ' : 'Previous: ') + n[1]);
    });
  }
  function render(d, from) {
    vwReady = false; clipsView.busy = null;
    const occ = occasionFor(d, from), O = F.OCC_BY[occ], wocc = wordsOccFor(d, from);
    const fromO = from && F.OCC_BY[from] && d.occ.includes(from) ? F.OCC_BY[from] : null;
    const crumbs = fromO ? [['Occasions', F.HREF.occasions()], [fromO.name, F.HREF.occasion(fromO.h)]]
      : d.se ? [['All cards', F.HREF.cards()], [F.seriesTitle(d.se), F.seriesHref(d.se)]] : [['All cards', F.HREF.cards()]];
    const href = SET ? window.PPSet.builderUrl(d.id, SET) : F.builderHref(occ, d.id, wocc);
    /* Daniel, 11 Oct: someone browsing a design wants THAT design, so the button is always
       "Design this card" (12 Oct; was "Design a card"), never "Continue card", and there is no "Start a new card" link. With a card
       already in progress it opens this cover as a new card (&new=1), as that link did. */
    const designHref = draft && !SET ? href + '&new=1' : href;
    const cta = '<a class="rdf-btn is-xl" href="' + esc(designHref) + '">Design this card</a>';
    /* Under the button, what a buyer most wants to know next (Daniel, 19 Oct: "remove the small text under Design this
       card ... and instead bring up those two points", green icons "as per Booking.com"). They were the first two of
       Printing and post, at the bottom; the rest of that section stays there. */
    const promise = '<ul class="rdf-buy__promise">' +
      '<li><span class="ic ic-printer" aria-hidden="true"></span>Printed and posted the next business day</li>' +
      '<li><span class="ic ic-truck" aria-hidden="true"></span>Arrives in 3–7 business days in the US</li></ul>';
    const lines = (s) => String(s || '').split(/\s*\/\s*/).filter(Boolean).map((t) => '<span>' + esc(t) + '</span>').join('');
    const words = [['On the front', d.text.front], ['Inside', d.text.inside]].filter((w) => w[1]);
    const occLinks = F.uniq(d.made.concat(d.suits)).filter((h) => F.OCC_BY[h]).map((h) => [F.OCC_BY[h].name, F.HREF.occasion(h)]);
    if (d.any) occLinks.push(['Any occasion', F.HREF.cards()]);
    const list = (k, param) => F.GET[k](d).map((v) => [F.valLabel(k, v), F.HREF.filter(param, v)]);
    const suits = SET ? [] : [['Occasions', occLinks], ['Who it’s for', list('who', 'who')], ['Feel', list('feel', 'feel')], ['Style', list('style', 'style')]].filter((g) => g[1].length);
    const all = F.designs();
    const num = (x) => parseInt(x.seNo, 10) || 999;
    const no = (x) => (x.se && x.seNo ? 'No. ' + (parseInt(x.seNo, 10) || x.seNo) + ' of ' + (parseInt(x.seOf, 10) || x.seOf) : '');
    const sameSeries = d.se ? all.filter((x) => x.se === d.se && x !== d).sort((a, b) => num(a) - num(b)) : [];
    const artist = F.byline(d);
    const byArtist = artist !== 'the Picturepops team' ? all.filter((x) => x !== d && F.byline(x) === artist && !sameSeries.includes(x)) : [];
    const motion = phone() || reduced() ? 'none' : 'tilt';
    const tiles = (xs) => xs.slice(0, 5).map((x) => F.tile(x, { occ: fromO ? fromO.h : '', motion, number: no(x) })).join('');
    const mores = [];
    if (SET) { /* nothing out of the set */ } else if (sameSeries.length) mores.push(['ms', 'More from ' + F.seriesTitle(d.se), 'See all ' + (sameSeries.length + 1), F.seriesHref(d.se), tiles(sameSeries)]);
    if (byArtist.length) mores.push(['ma', 'More by ' + artist, 'About ' + artist, F.artistHref(artist), tiles(byArtist)]);
    else if (!SET && artist !== 'the Picturepops team' && F.isPerson(artist)) mores.push(['ma', 'About the artist', 'Meet ' + artist, F.artistHref(artist), '']);
    const price = F.CFG.price ? '<span>' + esc(F.CFG.price) + '</span>' + (F.CFG.compare ? '<s aria-label="was ' + esc(F.CFG.compare) + '">' + esc(F.CFG.compare) + '</s>' : '') : '';

    const credit = SET ? F.creditLine(d, true).replace(/<a href="[^"]*">([^<]*)<\/a>/g, '<b>$1</b>') : F.creditLine(d, true);
    body.innerHTML =
      '<div class="rdf-lbgrid">' +
        '<div class="rdf-lbstage"><div class="rdf-stage" data-rdf-stage>' + stage(d, wocc) + '</div>' +
          '<div class="rdf-views" role="tablist" aria-label="See the card">' + TABS.map(([v, l]) =>
            '<button type="button" role="tab" aria-selected="false" data-view="' + v + '">' + l + '</button>').join('') + '</div></div>' +
        '<div class="rdf-dinfo">' +
          '<div class="rdf-dinfo__head">' + (SET ? '' : '<nav aria-label="Breadcrumb"><ol class="rdf-crumb">' + crumbs.map(([l, h]) => '<li><a href="' + esc(h) + '">' + esc(l) + '</a></li><li aria-hidden="true">›</li>').join('') +
            '<li aria-current="page">' + esc(d.n) + '</li></ol></nav>') +
            '<h1 class="rdf-h1 is-design" id="rdf-lb-title">' + esc(d.n) + '</h1>' + credit +
            (d.se && !SET ? '<a class="rdf-serieslink" href="' + esc(F.seriesHref(d.se)) + '"><span>' + esc(F.seriesTitle(d.se)) + '</span><span>' + (d.seNo ? esc(no(d)) : 'See the series') + '</span></a>' : '') + '</div>' +
          '<div class="rdf-buy"><p class="rdf-buy__price">' + price + '</p><div class="rdf-buy__go">' + cta + '</div>' + promise + '</div>' +
          (d.desc ? '<p class="rdf-desc">' + esc(d.desc) + '</p>' : '') +
          (words.length ? '<section class="rdf-dsec" aria-labelledby="rdf-words"><h2 id="rdf-words" class="rdf-h3">The words on the card</h2><dl class="rdf-words-dl">' +
            words.map(([w, t]) => '<dt>' + esc(w) + '</dt><dd>' + lines(t) + '</dd>').join('') + '</dl></section>' : '') +
          (d.text.back ? '<section class="rdf-story" aria-labelledby="rdf-story"><h2 id="rdf-story" class="rdf-over">The story on the back</h2><p>' + esc(d.text.back) + '</p></section>' : '') +
          (suits.length ? '<section class="rdf-dsec" aria-labelledby="rdf-suits"><h2 id="rdf-suits" class="rdf-h3">What it suits</h2><dl class="rdf-suits">' +
            suits.map(([t, vs]) => '<div><dt>' + esc(t) + '</dt><dd>' + vs.map(([l, h]) => '<a href="' + esc(h) + '">' + esc(l) + '</a>').join('') + '</dd></div>').join('') + '</dl></section>' : '') +
          '<section class="rdf-facts" aria-labelledby="rdf-facts"><h2 id="rdf-facts" class="rdf-over">Printing and post</h2><ul>' +
            '<li><span class="ic ic-truck" aria-hidden="true"></span>It goes by USPS letter mail, so there is no tracking.</li>' +
            (SET ? '' : '<li><span class="ic ic-mail" aria-hidden="true"></span>Shipping is free in the US. Elsewhere it’s $3 a card, and free on 3 or more.</li>') + '</ul></section>' +
        '</div>' +
      '</div>' +
      (mores.length ? '<div class="rdf-mores">' + mores.map(([k, t, l, h, cards]) => '<section aria-labelledby="rdf-' + k + '"><div class="rdf-mores__head"><h2 id="rdf-' + k + '" class="rdf-h3">' + esc(t) + '</h2>' +
        '<a class="rdf-link is-arrow" href="' + esc(h) + '">' + esc(l) + '<span class="ic ic-arrow-r" aria-hidden="true"></span></a></div>' + (cards ? '<ul class="rdf-grid is-five">' + cards + '</ul>' : '') + '</section>').join('') + '</div>' : '') +
      '<div class="rdf-buybar"><span class="rdf-buybar__price">' + price + '</span>' +
        '<a class="rdf-btn" href="' + esc(designHref) + '">Design this card</a></div>';
  }

  /* ---- the cursor label and the look close (Daniel, 11 Oct) ----------------------------------------------------------
     "I want a little label that follows the cursor that says 'Click to open', and on click it opens. The label can be
     designed the same way as the label in the builder's Edit pictures step ... Once open, I want the label to say
     'Click to zoom' or similar, so you can zoom in and scroll around to see the card's detail."

     The label is the builder's own: .pp-hovertag (cursor-tag.css) on its spring (PP.CursorTag, cursor-tag.js), shared,
     not copied. Over the shut card (Front or Back) a click opens it exactly as the Inside button does (that button's
     click); over the open card it zooms, and the zoomed card moves by a drag, the wheel or trackpad, and the arrow keys
     (PP.Roam, the same roam as the builder's Preview), and a click that did not move zooms out. Only over the card: off
     it, and over Front / Inside / Back (outside the stage), the label goes. A pointer device only; a phone gets a line
     under the card in the builder's touch words, and the same taps. Reduced motion: the label follows without the lag. */
  const TAG = window.PP && window.PP.CursorTag ? window.PP.CursorTag.create() : null;
  const coarse = () => window.matchMedia('(hover: none)').matches;
  let roam = null, tagOffT = 0, lastPt = null;
  // What zooms: the four states' Inside (CLIPS_ON), else the builder-pose Inside card. The same API.
  const R3 = () => (CLIPS_ON ? window.RDF3D && window.RDF3D.views : window.RDF3D);
  const zoomedNow = () => !!(R3() && R3().isZoomed && R3().isZoomed());
  // What a click on the card does now: 'open' (the shut card), 'zoom', 'out' (zoomed), or '' (moving, loading).
  function lookAction() {
    if (view === 'spin') return '';               // the turning card is only looked at
    /* The four states (Daniel, 17 Oct: "we can also remove the click to open i think, and instead have click (or tap on
       mobile) to zoom"): a click never changes the view, the tabs do; on any state at rest it zooms, and out again. */
    if (CLIPS_ON) {
      const V = window.RDF3D && window.RDF3D.views;
      if (!vwReady || !V || V.moving()) return '';
      return V.isZoomed() ? 'out' : V.canZoom() ? 'zoom' : '';
    }
    if (view !== 'inside') return 'open';
    const R = R3();
    if (!R || !(CLIPS_ON ? vwReady : shown3d())) return '';
    if (R.isZoomed && R.isZoomed()) return 'out';
    return R.canZoom && R.canZoom() ? 'zoom' : '';
  }
  function lookText(a) {
    if (coarse()) return a === 'open' ? 'Tap the card to open it' : a === 'zoom' ? 'Tap to zoom' : a === 'out' ? 'Drag to move · tap to zoom out' : '';
    return a === 'open' ? 'Click to open' : a === 'zoom' ? 'Click to zoom' : a === 'out' ? 'Click to zoom out' : '';
  }
  // Over the card itself: the flat card, a plane of the 3D one, or anywhere while zoomed (the card fills the stage).
  function overCard(t) {
    if (!t || !t.closest) return false;
    if (zoomedNow()) return true;
    if (CLIPS_ON) return !!t.closest('.rdf-vw .pp-plane');
    return !!t.closest(view === 'inside' ? '.pp-plane' : '.rdf-flip__face');
  }
  function lookSync() {
    const st = parts().stage;
    if (!st) return;
    const a = lookAction(), z = zoomedNow();
    st.classList.toggle('is-zoomed', z);
    st.classList.toggle('is-lookable', !!a && !coarse());
    const tag = st.querySelector('[data-rdf-tag]');
    const txt = lookText(a);
    if (tag && tag.textContent !== txt) tag.textContent = txt;
    if (!a && TAG && TAG.el === tag) TAG.off();
    /* The card came to rest under a cursor that is not moving (it opened, or zoomed out): the label is there at
       once, not on the next move. */
    if (a && TAG && tag && TAG.el !== tag && lastPt && !coarse()) {
      const under = document.elementFromPoint(lastPt.x, lastPt.y);
      if (under && st.contains(under) && overCard(under)) TAG.on(tag, st, lastPt.x, lastPt.y);
    }
    const hint = st.querySelector('[data-rdf-hint]');
    if (hint) {
      const t = coarse() ? lookText(a) : '';          // a desktop has the cursor label; no "Drag or scroll" line (Daniel, 11 Oct)
      hint.textContent = t; hint.hidden = !t;
    }
  }
  function tagAt(e) {
    const st = parts().stage, tag = st && st.querySelector('[data-rdf-tag]');
    if (!TAG || !tag || coarse()) return;
    lastPt = { x: e.clientX, y: e.clientY };
    const txt = lookText(lookAction());
    if (tag.textContent !== txt) { lookSync(); }
    if (!lookAction() || !overCard(e.target)) {
      if (TAG.el === tag && !tagOffT) tagOffT = setTimeout(() => { tagOffT = 0; TAG.off(); }, 90);   // across a gap between planes, it glides on
      return;
    }
    clearTimeout(tagOffT); tagOffT = 0;
    if (TAG.el !== tag) TAG.on(tag, st, e.clientX, e.clientY);
    else TAG.aim(e.clientX, e.clientY);
  }
  function roamer() {
    if (roam || !window.PP || !window.PP.Roam) return roam;
    roam = window.PP.Roam.create({
      geom: () => (R3() && R3().zoomGeom ? R3().zoomGeom() : null),
      write: (x, y, smooth) => { if (R3()) R3().zoomPan(x, y, smooth); },
    });
    return roam;
  }
  function zoomOut() { if (R3() && R3().zoomOut) R3().zoomOut(); lookSync(); }
  function lookClick(e) {
    const st = parts().stage;
    if (!st || !st.contains(e.target)) return false;
    const r = roam;
    if (r && r.tapAfterDrag()) return true;        // the click that ends a drag is not a zoom out
    const a = lookAction();
    if (!a || (a !== 'out' && !overCard(e.target))) return false;
    if (a === 'open') {
      const b = body.querySelector('[data-view="inside"]');
      if (b) b.click();
    } else if (a === 'zoom') {
      if (R3().zoomIn()) { const ro = roamer(); if (ro) ro.reset(0, 0); }
    } else zoomOut();
    lookSync();
    return true;
  }
  body.addEventListener('pointermove', (e) => {
    if (!parts().stage || !parts().stage.contains(e.target)) return;
    if (roam && roam.move(e)) return;
    tagAt(e);
  });
  body.addEventListener('pointerover', (e) => { if (parts().stage && parts().stage.contains(e.target)) { lookSync(); tagAt(e); } });
  body.addEventListener('pointerout', (e) => {
    const st = parts().stage;
    if (st && !st.contains(e.relatedTarget) && st.contains(e.target)) { lastPt = null; if (TAG && TAG.el) TAG.off(); }
  });
  body.addEventListener('pointerdown', (e) => {
    const st = parts().stage;
    if (!st || !st.contains(e.target) || !zoomedNow()) return;
    const r = roamer();
    if (r && r.down(e)) { try { st.setPointerCapture(e.pointerId); } catch (err) { /* best-effort */ } st.classList.add('is-grabbing'); }
  });
  const lift = () => { if (roam) roam.up(); const st = parts().stage; if (st) st.classList.remove('is-grabbing'); };
  body.addEventListener('pointerup', lift);
  body.addEventListener('pointercancel', lift);
  body.addEventListener('wheel', (e) => {
    const st = parts().stage;
    if (!st || !st.contains(e.target) || !zoomedNow() || e.ctrlKey) return;
    const r = roamer();
    if (r) r.wheel(e);
  }, { passive: false });

  /* ---- open and close ------------------------------------------------------------------------------------------ */
  const behind = () => document.querySelectorAll('.sn-wrap, main, footer.ft, .dock, .rdf-sheet');
  /* THE DESIGN'S OWN PAGE IS THE ONE TO INDEX (2026-10-12, the real-pages plan, phase 4). Every design has a page at
     /pages/cards/<page> (templates/metaobject/design.liquid); this box stays the way through the lists, and while it is
     open the page's canonical (and og:url) name the design's page, so a search engine that reaches this address,
     shared or crawled, is told where the card lives. Closing puts the list's own back. */
  const canon = document.querySelector('link[rel="canonical"]'), ogUrl = document.querySelector('meta[property="og:url"]');
  const listCanon = canon ? canon.getAttribute('href') : '', listOg = ogUrl ? ogUrl.getAttribute('content') : '';
  function canonFor(d) {
    const u = d && d.page ? location.origin + '/pages/cards/' + d.page : '';
    if (canon) canon.setAttribute('href', u || listCanon);
    if (ogUrl) ogUrl.setAttribute('content', u || listOg);
  }
  function show(u) {
    const p = new URL(u, location.href).searchParams;
    const d = F.byId(p.get('design') || '');
    const wasOpen = !lb.hidden;
    autoStop();
    if (!wasOpen) listTitle = document.title;
    release3d();   // one 3D card at a time: the last design's goes, and its images with it
    clearLater(); openTok++; clearTimeout(spinOffT);
    dropArt();
    current = d;
    const want = SHOW_IN[p.get('show')] || 'front';
    view = 'front';
    if (!d) {
      body.innerHTML = '<div class="rdf-lbgone">' + F.notice('empty', 'That design is not here any more.', 'Every card we make is on All cards.') + '<p><a class="rdf-btn" href="' + esc(F.HREF.cards()) + '">See every card</a></p></div>';
      document.title = 'Design not found · Picturepops';
    } else {
      render(d, p.get('from') || '');
      /* Front, the default: the flat card at once, the card of the states fading in over it when it is ready. An address
         that names a view: Back is shown at once, Inside opens, as from the tab; Spin fades in over the flat card when
         its clip is in, and the card of the states loads after it (toSpin), out of the layout until a state is picked. */
      if (want === 'spin') setView('spin', true);
      else {
        setView('front', false);
        if (want === 'back') setView('back', false);
        else if (want === 'inside') setView(want, true);
      }
      if (want === 'front') autoStart(d); else autoStop();
      document.title = d.n + ' · Picturepops';
      canonFor(d);
      lookSync();          // a phone's line under the card is there before any touch
    }
    lb.hidden = false;
    document.documentElement.classList.add('rdf-lb-open');
    behind().forEach((el) => { el.inert = true; });
    panel.scrollTop = 0;
    if (!wasOpen || !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
  }
  let listTitle = '';
  function hide() {
    if (lb.hidden) return;
    autoStop();
    lb.hidden = true;
    release3d();
    clearLater(); openTok++; clearTimeout(spinOffT);
    current = null;
    dropArt();
    document.documentElement.classList.remove('rdf-lb-open');
    behind().forEach((el) => { el.inert = false; });
    if (listTitle) document.title = listTitle;
    canonFor(null);
    const back = opener && opener.isConnected ? opener : null;
    if (back) back.focus({ preventScroll: true });
    opener = null;
  }
  /* Close: back through every design opened in this box, to the list. A design's address opened directly has no list
     entry behind it, so it becomes the list's own address instead (its occasion's, else All cards). */
  function close() {
    if (depth > 0) { closing = true; history.go(-depth); return; }
    history.replaceState(null, '', listHref || listFor(location.href));
    hide();
  }
  function listFor(u) {
    if (SET) return location.pathname;
    const p = new URL(u, location.href).searchParams, from = p.get('from');
    return from && F.OCC_BY[from] ? F.HREF.occasion(from) : F.HREF.cards();
  }
  window.addEventListener('popstate', () => {
    const st = history.state || {};
    depth = st.rdfLb || 0;
    if (isDesign(location.href)) {
      if (closing && !depth) { closing = false; history.replaceState(null, '', listFor(location.href)); hide(); return; }
      show(location.href);
    } else { closing = false; hide(); }
  });

  /* The card's own show stops for good the moment the visitor does anything in the box (above, AUTO). Not on a hover:
     the shut card tilting to the pointer is not a choice. */
  ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((t) => lb.addEventListener(t, autoStop, { capture: true, passive: true }));

  /* A design's link anywhere on the page opens here, unless it is meant for a new tab. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-lb-close]')) { e.preventDefault(); close(); return; }
    const arrow = e.target.closest('[data-step]');
    if (arrow && lb.contains(arrow)) { stepView(+arrow.getAttribute('data-step')); return; }
    if (!lb.hidden && lookClick(e)) return;
    const v = e.target.closest('[data-view]');
    if (v && lb.contains(v)) {
      setView(v.getAttribute('data-view'), true);
      const q = new URLSearchParams(location.search);
      if (view === 'front') q.delete('show'); else q.set('show', view);
      history.replaceState(history.state, '', location.pathname + '?' + q.toString());
      return;
    }
    const a = e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target) return;
    if (!isDesign(a.href) || !F.designs()) return;
    e.preventDefault();
    if (lb.hidden) { opener = a; listHref = location.pathname + location.search; }
    depth += 1;
    history.pushState({ rdfLb: depth }, '', a.getAttribute('href'));
    show(a.href);
  });
  document.addEventListener('keydown', (e) => {
    if (lb.hidden) return;
    if (zoomedNow()) {
      if (e.key === 'Escape') { e.preventDefault(); zoomOut(); return; }
      const inField = e.target.closest && e.target.closest('[role="tab"], input, textarea, select');
      if (!inField && roamer() && roam.key(e)) return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    const tab = e.target.closest && e.target.closest('[role="tab"]');
    if (tab && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      const tabs = [...body.querySelectorAll('[role="tab"]')], i = tabs.indexOf(tab), n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      n.focus(); n.click(); e.preventDefault(); return;
    }
    /* Anywhere else in the box, ArrowLeft / ArrowRight step the view as the arrows do: never while zoomed (the roam,
       above, has them then), never in a field, never with a modifier (the browser's own back and forward). On a tab
       the tab list's own roving focus (above) already does the same and moves the focus with it. */
    if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey && !zoomedNow() &&
      !(e.target.closest && e.target.closest('input, textarea, select, [contenteditable]')) && body.querySelector('[data-view]')) {
      e.preventDefault();
      stepView(e.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    if (e.key === 'Tab') {   // keep the focus inside the box
      const f = [...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* A design's own address, opened directly: the box opens over the list as soon as the cards are here. */
  if (isDesign(location.href)) {
    F.load().then(() => F.loadPool().catch(() => null)).then(() => { listHref = listFor(location.href); show(location.href); })
      .catch(() => { lb.hidden = false; body.innerHTML = '<div class="rdf-lbgone">' + F.notice('error', 'The card did not load. Refresh the page to try again.') + '</div>'; });
  } else {
    F.loadPool().catch(() => null);   // so credits link to the Pool's artists when a box opens
  }
})();
